import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import UserCreateModal from './UserCreateModal';
import UserEditRoleModal from './UserEditRoleModal';
import API_CONFIG from '../../config/api';

const UserManagement = ({ authenticatedUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resetPasswordModal, setResetPasswordModal] = useState({ open: false, user: null });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('stratovai_access_token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Check if response is JSON or HTML
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Failed to fetch users: ${response.status}`);
        } else {
          // HTML error page - read and discard to clear response body
          await response.text();
          throw new Error(`Server error (${response.status}): ${response.statusText}`);
        }
      }

      // Verify response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response. Please check API endpoint.');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      const errorMessage = err.message || 'Failed to fetch users';
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateUser = () => {
    setCreateModalOpen(true);
  };

  const handleEditRole = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleDeactivate = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}? They will no longer be able to log in.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('stratovai_access_token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/${userId}/deactivate`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to deactivate user');
      }

      setSnackbar({ open: true, message: 'User deactivated successfully', severity: 'success' });
      fetchUsers();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleUserCreated = () => {
    setCreateModalOpen(false);
    fetchUsers();
  };

  const handleRoleUpdated = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
    fetchUsers();
  };

  const handleResetPassword = (user) => {
    setResetPasswordModal({ open: true, user });
    setResetPasswordResult(null);
  };

  const handleResetPasswordConfirm = async () => {
    if (!resetPasswordModal.user) return;
    
    setResetPasswordLoading(true);
    try {
      const token = localStorage.getItem('stratovai_access_token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/${resetPasswordModal.user.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reset password');
      }

      const result = await response.json();
      setResetPasswordResult(result);
      setSnackbar({
        open: true,
        message: 'Password reset successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to reset password: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleResetPasswordClose = () => {
    setResetPasswordModal({ open: false, user: null });
    setResetPasswordResult(null);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'project_admin':
        return 'warning';
      case 'project_manager':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    return role?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'User';
  };

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No users found matching your search.' : 'No users found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    {user.full_name}
                    {user.force_password_change && (
                      <Chip
                        label="Password Change Required"
                        size="small"
                        color="warning"
                        sx={{ ml: 1, fontSize: '0.7rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Active' : 'Inactive'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditRole(user)}
                      title="Edit Role"
                      disabled={!user.is_active}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {authenticatedUser?.role === 'super_admin' && (
                      <IconButton
                        size="small"
                        onClick={() => handleResetPassword(user)}
                        title="Reset Password"
                        disabled={!user.is_active}
                        color="warning"
                      >
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDeactivate(user.id, user.full_name)}
                      title="Deactivate User"
                      disabled={!user.is_active}
                      color="error"
                    >
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UserCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUserCreated={handleUserCreated}
        onShowSnackbar={(message, severity) => setSnackbar({ open: true, message, severity })}
      />

      <UserEditRoleModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onRoleUpdated={handleRoleUpdated}
        onShowSnackbar={(message, severity) => setSnackbar({ open: true, message, severity })}
      />

      {/* Reset Password Modal */}
      {resetPasswordModal.open && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={handleResetPasswordClose}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 4,
              maxWidth: 500,
              width: '90%',
              boxShadow: 3
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!resetPasswordResult ? (
              // Confirmation step
              <>
                <Typography variant="h6" gutterBottom>
                  Reset Password
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Reset password for <strong>{resetPasswordModal.user?.full_name}</strong> • {resetPasswordModal.user?.email}?
                </Typography>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  This will revoke active sessions and require a new password at next login.
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={handleResetPasswordClose} disabled={resetPasswordLoading}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleResetPasswordConfirm}
                    disabled={resetPasswordLoading}
                    startIcon={resetPasswordLoading && <CircularProgress size={16} />}
                  >
                    {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </Box>
              </>
            ) : (
              // Success step - show new password
              <>
                <Typography variant="h6" gutterBottom>
                  Temporary Password Generated
                </Typography>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Password reset successfully! Share this password securely with the user.
                </Alert>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    User: {resetPasswordResult.user_email}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Temporary Password"
                    value={resetPasswordResult.temporary_password}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '1.1rem' }
                    }}
                    sx={{ mb: 2 }}
                  />
                </Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  ⚠️ Shown only once. Share securely. User must change it on first login.
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={handleResetPasswordClose}>
                    Close
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;

